/**
 * @module Bubbles
 */

import { BehaviorSubject, Subject, Subscription, filter } from "rxjs";
import { DialInPhoneNumber } from 'rainbow-web-sdk/models/dial-in-phone-numbers.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Contact } from "./contact.model";
import { User } from "./user.model";
import { BUBBLE_SVC, BubbleService } from "rainbow-web-sdk/services/bubbles/bubble.service";
import { MainService } from "rainbow-web-sdk/services";
import { CONTACT_SVC, ContactService } from "../services/core/contact.service";
import { BUBBLE_TAG_SVC, BubbleTagService } from "../services/bubbles/bubbleTag.service";

/**
 * Bubble user role
 * Each role gives access to different functionalities in Bubbles.
 */
export enum BubbleUserRole {
    /**
     * Users are basic members who have accepted an invitation to join a Bubble.
     * They can then send messages in the bubble, join a Web Conference and leave the Bubble.
     * Users have no right to manage the bubble and the members in this one.
     */
    USER = "user",

    /**
     * Organizers are bubble members who have been promoted to manage the bubble and the members in this one.
     * Organizers can be promoted by the owner or by another organizer.
     * They can also subsequently be demoted to basic member by another owner or organizer.
     * Organizers have the ability to invite or remove members/guests in the bubble, to share the bubble through a public direct link.
     * They can send messages in the bubble, start/join a Web Conference and leave the Bubble.
     */
    // !! IMPORTANT WARNING !! Don't change string value "moderator" for this enum
    ORGANIZER = "moderator",

    /**
     * @internal
     */
    GUEST = "guest"
}

/**
 * Status of a user in the bubble
 */
export enum BubbleUserStatus {
    /**
     * The user has been invited to join the bubble.
     */
    INVITED = 'invited',

    /**
     * The user has accepted the invitation to join the bubble and is an active member.
     */
    ACCEPTED = 'accepted',

    /**
     * The user has declined the invitation to join the bubble.
     */
    REJECTED = 'rejected',

    /**
     * This status applies to 3 situations:
     * - The user has been removed from the bubble by the owner or one of the organizers.
     * - The user left the bubble on his own and has not requested the creation of an archive for this bubble.
     * - The user has deleted the archive of a bubble he previously left
     */
    DELETED = 'deleted',

    /**
     * The user left the bubble of his own.
     * During this operation, the user has requested the creation of an archive for this bubble.
     */
    UNSUBSCRIBED = 'unsubscribed',
}

/**
 * Type of message history to be returned to a new bubble user.
 */
export enum BubbleHistoryType {
    /**
     * All the previous messages history are provided to a new bubble user
     */
    ALL = "all",

    /**
     * No message history is provided to a new bubble user
     */
    NONE = "none"
}

/**
 * Visibility of the bubble
 */
export enum BubbleVisibility {
    /**
     * A private bubble can be joined only by invitation or via a public link if the bubble has been shared (bubble configuration parameter).
     */
    PRIVATE = 0,

    /**
     * A public bubble can be found via a search operation (using 'searchBubbles()' method) or get by its identifier
     * even if a user is not a member of this bubble. Information on such bubbles are provided for guidance only.
     */
    PUBLIC = 1
}

/**
 * @eventProperty
 * `Bubble` specific events
 * Can listen to it via `bubble.subscribe()` API
 */
export enum BubbleEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has accepted the invitation in the bubble.
     *
     * **Event Data**
     * - **bubbleUser**: The bubble user who has accepted the invitation
    */
    ON_USER_INVITATION_ACCEPTED = 'ON_USER_INVITATION_ACCEPTED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has declined the invitation to join the bubble.
     *
     * **Event Data**
     * - **bubbleUser**: The bubble user who has declined the invitation
     */
    ON_USER_INVITATION_DECLINED = 'ON_USER_INVITATION_DECLINED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has left the bubble of his own
     * **Event Data**
     * - **bubbleUser**: The bubble user who has left the bubble
     */
    ON_USER_BUBBLE_LEFT = 'ON_USER_BUBBLE_LEFT',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has been removed from the bubble by the owner or one of the organizers
     * **Event Data**
     * - **bubbleUser**: The bubble user who has been removed from the bubble
     */
    ON_USER_BUBBLE_REMOVED = 'ON_USER_BUBBLE_REMOVED',

    /**
     * @eventProperty
     * This RBEvent is sent when the bubble avatar is updated
     */
    ON_AVATAR_UPDATE = 'ON_AVATAR_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is sent when the dialInCode is updated
     */
    ON_DIAL_IN_CODE_UPDATE = 'ON_DIAL_IN_CODE_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is sent when the includeAllPhoneNumber is updated
     */
    ON_INCLUDE_ALL_PHONENUMBER_UPDATE = 'ON_INCLUDE_ALL_PHONENUMBER_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is sent when the dialInPhoneNumber is updated
     */
    ON_DIAL_IN_PHONENUMBER_UPDATE = 'ON_DIAL_IN_PHONENUMBER_UPDATE',

    /**
     * @eventProperty
     * This RB event is sent when the user's role is updated
     */
    ON_USER_ROLE_UPDATE = 'ON_USER_ROLE_UPDATE',

    /**
     * @internal
     * @eventProperty
     * @param lobbyUsers - the updated list of lobby users
     * This RB event is send when the list of users in the waiting lobby is updated
     */
    ON_USERS_IN_LOBBY_UPDATE = 'ON_USERS_IN_LOBBY_UPDATE',

    /**
     * @internal
     * @eventProperty
     * @param status - The current status of the request of type AccessRequestStatus
     * This RB event is send when the connected user's request to join bubble is updated (or created)
     */
    ON_MY_USER_IN_LOBBY_UPDATE = 'ON_MY_USER_IN_LOBBY_UPDATE',

    /**
     * @eventProperty
     * This RB event is send when the conference attached to this bubble is started
     */
    ON_CONFERENCE_START_EVENT = 'ON_CONFERENCE_START_EVENT',

    /**
     * @eventProperty
     * This RB event is sent when the conference attached to this bubble is Stopped
     */
    ON_CONFERENCE_STOP_EVENT = 'ON_CONFERENCE_STOP_EVENT',

    /**
     * @eventProperty
     * This RB event is sent when the conference attached to this bubble is Stopped
     */
    ON_TAGS_UPDATED = 'ON_TAGS_UPDATED',

    /**
     * @eventProperty
     * This RB event is sent when the bubble password configuration has changed (or password is reset)
     */
    ON_BUBBLE_PASSWORD_UPDATED = 'ON_BUBBLE_PASSWORD_UPDATED',

    /**
     * @eventProperty
     * This RB WTF event is sent when something happen !!!
     * Temporary event, please don't use this stuff
     */
    ON_DATA_UPDATED = 'ON_DATA_UPDATED'
}

/**
 * Bubble data that can be cleaned
 */
export enum BubbleCleanupData {
    /**
     * All rooms IM will be removed for all room members, all pins will also be removed
     */
    IM = "im",

    /**
     * All pools associated to that room will be removed
     */
    POLL = "poll",

    /**
     * That rooms won't be viewer of any document (Document's list will appear empty)
     */
    DOCUMENT = "document",

    /**
     *  All members of this room that is not an organizer will be removed
     */
    MEMBER = "member",

    /**
     * All recordings will be removed
     */
    RECORD = "record"
}

/*
 * This interface is used to represent the list of types for receivedFilePolicy
 * Depending on the value, there may be some restriction regarding receiving files
 */
export enum BubbleReceivedFilePolicyValue {
    ENABLED = "enabled",
    DISABLED = "disabled",
    INTERNAL_ONLY = "internalOnly"
}

/*
 * This interface is used to represent the list of types for  and shareFileCustomisation
 * Depending on the value, the contact may have some restriction regarding receiving files
 */
export enum BubbleShareFileCustomisationValue {
    ENABLED = "enabled",
    DISABLED = "disabled"
}

/**
 * This interface defines the settings that can be sent to the setSettings bubble API
 */
export interface BubbleSettings {
    /**
     * The bubble name
     */
    name?: string;

    /**
     * The bubble description
     */
    description?: string;

    /**
     * A user can create a bubble and not have to register users. He can share instead a public link also called 'public URL'(users public link).
     * Depending on the autoRegister value, if another person uses the link to join the bubble :
     * autoRegister = 'unlock':
     * If this user is not yet registered inside this bubble, he is automatically included with the status 'accepted' and join the bubble.
     * autoRegister = 'lock':
     * If this user is not yet registered inside this bubble, he can't access to the bubble. So that he can't join the bubble.
     * autoRegister = 'unlock_ack' (value not authorized yet):
     * If this user is not yet registered inside this bubble, he can't access to the bubble waiting for the bubble's owner acknowledgment.
     */
    autoRegister?: string;

    /**
     * Invited users will be auto-added in the bubble
     */
    autoAcceptInvitation?: boolean;

    /**
     * In case of conference in this bubble, all participants will be muted upon join
     */
    muteUponEntry?: boolean;

    /**
     * In the event of a conference in this bubble, specify whether a local tone should be played each time a participant joins the conference.
     */
    playEntryTone?: boolean;

    /**
     * Indicates if user chooses to include all Dial In phone numbers
     */
    includeAllPhoneNumbers?: boolean;

    /**
     * Content to clear automatically at conference end (only room creator can modify it)
     */
    contentToAutoClear?: {
        im: boolean;
        poll: boolean;
        document: boolean;
        member: boolean;
    };

    recordingAndTranscription?: ConferenceRecordingConfig;
}

/**
 * In order to classify and find a bubble quickly and easily, a user can decide to assign tags to it.
 *
 * A tag is defined by a name and a color
 * @module BubbleTags
 */
export interface BubbleTag {
    /**
     * The Name of the tag
     */
    name: string;

    /**
     * The color of the tag in hexadecimal color code (ex #ff9900)
     */
    color?: string;

    /**
     * The tagged bubble identifiers
     * @internal
     */
    bubbleIds?: string[];
}

export interface BubbleUser {
    /**
     * The user object related to the user
     * @readonly
     */
    user: User;

    /**
     * Date when the user has been added in the bubble
     * @readonly
     */
    date: Date;

    /**
     * Role of the user in the bubble
     * @readonly
     */
    role: BubbleUserRole;

    /**
     * The status of the user in the bubble.
     * @readonly
     */
    status: BubbleUserStatus;

    /**
     * The ID of the user that has invited us to the bubble
     * @readonly
     */
    invitingUserId?: string;
}

export interface BubbleInvitation {

    // ----------------------------------------------------
    // INTERFACE DATA
    // ----------------------------------------------------

    /**
     * The bubble database Identifier
     * @readonly
     */
    dbId: string;

    /**
     * The name of the bubble
     * @readonly
     */
    name: string;

    /**
     * A description of the purpose of the bubble
     * @readonly
     */
    description: string;

    /**
     * The bubble avatar
     * @readonly
     */
    avatar: string;

    /**
     * Date when the user has been added in the bubble
     * @readonly
     */
    invitationDate?: Date;

    /**
     * Role of the invited user in the bubble during the invitation step
     * @readonly
     */
    invitationRole?: BubbleUserRole;

    /**
     * The ID of the user that has invited us to the bubble
     * @readonly
     */
    invitingUserId?: string;

    // ----------------------------------------------------
    // INTERFACE API
    // ----------------------------------------------------

    /**
     * Accept invitation to join a bubble
     */
    acceptInvitation(): Promise<Bubble>;

    /**
     * Decline invitation to join a bubble
     */
    declineInvitation(): Promise<Bubble>;

    /**
     * Specifies whether the invitation is to join a bubble or a webinar
     */
    isWebinarInvitation(): boolean;

    /**
     * Method to return a simplified JSON representation of the Bubble object
     */
    toJSON(): any;
}

export interface ConferenceRecordingConfig {

    // ----------------------------------------------------
    // INTERFACE DATA
    // ----------------------------------------------------

    /**
     * The name of the record file
     */
    recordingName?: string;

    /**
     * Will recording be automatically deleted after 15 days? Only transcription will remains
     */
    persistent?: boolean;

    /**
     * Is transcription requested?
     */
    transcription?: boolean;

    /**
     * Is transcript requested?
     */
    summary?: boolean;

    /**
     * Form for summary
     */
    summaryForm?: string;

    /**
     * Prompt id for summary
     */
    summaryPrompt?: string;

    /**
     * The input language used for live transcription
     */
    inputLanguageLiveTranscription?: string;

    /**
     * The input language used for transcription
     */
    inputLanguageTranscription?: string;
}

/**
 * The saved informations inside the bubble regarding conferences
 */
export interface BubbleConferenceInfo {
    /**
     * If all numbers are included for the dial-in function
     */
    includeAllPhoneNumbers?: boolean;

    /**
     * Are users muted upon entry in the conference. Managed only by owner
     */
    muteUponEntry?: boolean;

    /**
     * The list of phone numbers avaialbe for dial-in
     */
    phoneNumbers?: string[];

    /**
     * If the client should play an entry tone when someone joins the conference
     */
    playEntryTone?: boolean;

    /**
     * Information about the current active conference session inside this bubble
     */
    sessions?: any[];

    /**
     * Information about the current configuration regarding recording and transcription
     */
    recordingAndTranscription?: ConferenceRecordingConfig;
}

export interface Bubble {
    conference: BubbleConferenceInfo;

    // ----------------------------------------------------
    // INTERFACE DATA
    // ----------------------------------------------------

    /**
     * The bubble database Identifier
     * @readonly
     */
    dbId: string;

    /**
     * The jid associated to this bubble
     * @readonly
     */
    jid: string;

    /**
     * The name of the bubble
     * @readonly
     */
    name: string;

    /**
     * A description of the purpose of the bubble
     * @readonly
     */
    description: string;

    /**
     * The bubble avatar
     * @readonly
     */
    avatar: string;

    /**
     * The visibility of the bubble
     * A public bubble can be found via a search operation (using 'searchBubbles()' method) or get by its identifier
     * even if the connectedUser is not a member of this bubble.
     * A private bubble can be joined only by invitation or via a public link if the bubble has been shared for this purpose.
     * @readonly
     */
    visibility: BubbleVisibility;

    /**
     * Allow newcomers to access or not messages history. If the value is "all", a newcomer can retrieve the full bubble messages history
     * @readonly
     */
    history: BubbleHistoryType;

    /**
     * When set to true, allows to automatically add participants in the bubble (default behavior is that participants need to accept the bubble invitation first before being a member of this bubble)
     * @readonly
     */
    autoAcceptInvitation: boolean;

    /**
     * A user can create a bubble and not have to register users. He can share instead a public link also called 'public URL'(users public link).
     * Depending on the autoRegister value, if another person uses the link to join the bubble :
     * autoRegister = 'unlock':
     * If this user is not yet registered inside this bubble, he is automatically included with the status 'accepted' and join the bubble.
     * autoRegister = 'lock':
     * If this user is not yet registered inside this bubble, he can't access to the bubble. So that he can't join the bubble.
     * autoRegister = 'unlock_ack' (value not authorized yet):
     * If this user is not yet registered inside this bubble, he can't access to the bubble waiting for the bubble's owner acknowledgment.
     * @readonly
     */
    autoRegister: string;

    /**
     * If the bubble has alert notification feature enabled
     * The notification feature must be enabled
     * @readonly
     */
    isAlertNotificationEnabled: boolean;

    /**
     * The owner of the bubble
     * @readonly
     */
    owner: User;

    /**
     * Information on the connected user in this bubble.
     * @readonly
     */
    bubbleConnectedUser: BubbleUser;

    /**
     * Custom data associated to the bubble
     * @readonly
     */
    customData: any;

    /**
     * The number of users of the bubble with the status 'accepted' (he joined the bubble) or 'invited'.
     * They are considered to be the actual participants in the bubble.
     * @readonly
     */
    nbParticipants: number;

    /**
     * The creation date of the bubble
     * @readonly
     */
    creationDate?: Date;

    /**
     * last activity date of bubble
     * @readonly
     */
    lastActivityDate?: Date;

    /**
     * Indicates whether the bubble is ready for chatting.
     * @readonly
     */
    ready: boolean;

    /**
     * Rainbow HUB specificity for Hunting Group .
     * Specifies whether the bubble is created as managed bubble.
     * In this case, there is no owner of the bubble; each member acts as organizer.
     * In addition, a group member cannot leave the bubble and cannot be removed from the bubble.
     * @readonly
     */
    isHuntingGroup: boolean;

    /**
     * The bubble is managed by a Rainbow user acting as the bubble administrator.
     * This user is not a member of the bubble (It is not known to other members) and does not, by default, have the role of owner.
     * But it can manage the list (settings, members), as if it were the owner. In fact, it can give itself this role.
     * @readonly
     */
    isOwnedByBubbleAdmin: boolean;

    /**
     * Bubble associated tags
     * @readonly
     */
    tags: BubbleTag[];

    /**
     * The bubble is managed by a Rainbow user acting as the bubble administrator.
     * This user is not a member of the bubble (It is not known to other members) and does not, by default, have the role of owner.
     * But it can manage the list (settings, members), as if it were the owner. In fact, it can give itself this role.
     */
    contentToClear: { im: boolean; document: boolean; poll: boolean; member: boolean; record: boolean; };

    /**
     * Define if the room restricts the receiving of files to protect from data coming from a foreign user of the company, for all of its users
     *   - ‘enabled’ Users can receive files without restrictions (default value)
     *   - ‘internalOnly’ Users can receive files only within internal company communications
     *   - ‘disabled’ Users can’t receive files under any circumstances. Best for users or roles where no file exchange is necessary or allowed. This ensures maximum protection against unauthorized or potentially harmful file transfers.
     */
    receivedFilePolicy: BubbleReceivedFilePolicyValue;

    /**
     * Defines the rights to upload file in a the bubble for the connected user
     */
    shareFileCustomisation: BubbleShareFileCustomisationValue;

    /**
     * Defined when receivedFilePolicy is internalOnly so that bubble affiliation to a given company can be checked
     */
    companyId: string;

    /**
     * True if the bubble is protected by password (when joining with external link)
     */
    hasPassword: boolean;

    /**
     * The current bubble password; Visible only for owner of the bubble
     */
    bubblePassword: string;

    // ----------------------------------------------------
    // INTERFACE API
    // ----------------------------------------------------
    /**
     * Permits to emit BubbleEvents events
     * Can be listened via bubble.subscribe() API
     * @param name - the name of the event
     * @param data - the data to be send (an object
     */
    sendEvent(name: BubbleEvents, data?: any): void;

    /**
     * Subscribe to updates on this bubble (all events are of `RBEvent` type)
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     */
    subscribe(handler: (event: RBEvent<BubbleEvents>) => any, eventNames?: BubbleEvents | BubbleEvents[]): Subscription;

    /**
     * Bubbles have a default avatar made up of the avatars of the first 4 participants in the bubble.
     * But this avatar can be overloaded by an image chosen by the user
     * @param base64AvatarImg - The image to be set
     * @param width - The width of the image to be set
     * @param height - The height of the image to be set
     */
    setCustomAvatar(base64AvatarImg: string, width?: number, height?: number): Promise<Bubble>;

    /**
     * Remove this bubble custom avatar (reset to default avatar)
     */
    removeCustomAvatar(): Promise<Bubble>;

    /**
     * Update the bubble settings
     * @param bubbleSettings - The settings to be updated in the bubble
     */
    setSettings(bubbleSettings: BubbleSettings): Promise<Bubble>;

    /**
     * Update the bubble custom data
     * @param customData - the custom data to be set to the bubble
     */
    setCustomData(customData: any): Promise<Bubble>;

    /**
     * Update user's role in the Bubble.
     * This operation is only authorised for the organizers and the owner of the bubble.
     * The user can be updated to ORGANIZER or USER.
     * @param user - The user that should be updated
     * @param role - New user role: ORGANIZER or USER
     */
    setUserRole(user: User, role: BubbleUserRole): Promise<Bubble>;

    /**
     * Change the owner of the bubble
     * @param newOwner - The user that should be the new owner
     */
    changeOwner(newOwner: User): Promise<Bubble>;

    /**
     * Add user to the bubble; Can be used to directly add user without invitation or as a organizer
     * @param user - The user to add in the bubble
     * @param role - The user role in this bubble ('user', 'organizer' or 'guest'), by default is 'user'
     * @param reason - Text that will be join in the invitation
     * @param withoutInvitation - When set to true no invitation is sent
     */
    addUser(user: User, role?: BubbleUserRole, reason?: string, withoutInvitation?: boolean): Promise<Bubble>;

    /**
     * Get all bubble users
     *
     * The 'role' parameter allows to get filtered user arrays (only users or only organizers)
     *
     * **Please note:** this method is asynchronous, as user loading is performed lazily to avoid weighing down the application.
     * @param role - users role (defaut value is 'ALL')
     */
    getUsers(role?: 'ALL' | 'USER' | 'ORGANIZER'): Promise<BubbleUser[]>;

    /**
     * Remove a user from the Bubble.
     * API only available for organizers / owner of the bubble
     * The user will no longer have access to this bubble nor the history
     * @param user - the contact that should be removed
     */
    removeUser(user: User): Promise<Bubble>;

    /**
     * Mass provisionning API, allow to invite users and guests to the bubble;
     * The guests will receive an email to join the bubble.
     * @param users - The users to add bubble
     * @param emails - List of emails to invite to the bubble
     * @param role - role to be applied to all invited users (organizer for instance)
     */
    inviteUsers(users: User[], emails?: string[], role?: BubbleUserRole): Promise<Bubble>;

    /**
     * Promote all users (basic member) of a bubble to organizer role.
     * This operation is only authorised for the owner of the bubble.
     */
    promoteAllUsers(): Promise<Bubble>;

    /**
     * Demote all organizers of a bubble, except the owner, to user (basic member) role.
     * This operation is only authorised for the owner of the bubble.
     */
    demoteAllUsers(): Promise<Bubble>;

    /**
     * User leaves the bubble.
     * If createArchive is TRUE, the bubble will be archived, i.e. the user can only read the
     * content that's already in the bubble, but no further action can be done inside the bubble
     * The participant will go to UNSUBSCRIBED state (read-only)
     * @param createArchive - If TRUE, the bubble will be archived for my user
     */
    leave(createArchive?: boolean): Promise<Bubble | undefined>;

    /**
     * Delete own bubble.
     * If createArchive is TRUE, the bubble will be archived, i.e. all users can only read the
     * content that's already in the bubble, but no further action can be done inside the bubble
     * All participants in the bubble will go to UNSUBSCRIBED state (read-only)
     * @param createArchive - If TRUE, the bubble will be archived for all users (i.e. read only mode for all users still inside);
     */
    delete(createArchive?: boolean): Promise<Bubble | undefined>;

    /**
     * Check if the given user is the owner of the bubble
     * @param user - The user to check
     */
    isUserOwner(user: User): boolean;

    /**
     * Check if the connected user is the owner of the bubble
     */
    isConnecteUserOwner(): boolean;

    /**
     * Check if the given user is an organizer of the bubble
     * @param user - The user to check
     */
    isUserOrganizer(user: User): boolean;

    /**
     * Check if the connected user is an organizer of the bubble
     */
    isConnecteUserOrganizer(): boolean;

    /**
     * Check if the connected user is a member of the bubble
     */
    isConnectedUserMember(): Promise<boolean>;

    /**
     * Indicates whether a conference has already been started in the bubble
     */
    isBubbleConferenceStarted(): boolean;

    /**
    * Method helper to know if bubble is a webinar bubble
    */
    isWebinarBubble(): boolean;

    /**
     * In case of active bubble conference, we might know the owner of the conference inside; This API will return the ID of the owner
     */
    getBubbleConferenceOwnerId(): string;

    /**
     * Method helper retrieve the name of the bubble as displayed in the logs
     */
    getNameForLogs(): string;

    /**
     * Get the bubble settings
     */
    getSettings(): BubbleSettings;

    /**
     * Method to retrieve all users of the bubble that have accepted
     */
    getAllAcceptedUsers(): BubbleUser[];

    /**
     * Method to retrieve conference.dialInCode code
     */
    getDialInCode(): string;

    /**
     * Method to retrieve conference.phoneNumbers array status
     */
    getPhoneNumbers(): DialInPhoneNumber[];

    /**
     * Method to create bubble "guest" access code
     */
    createBubbleLink(): Promise<string>;

    /**
     * Method to delete bubble "guest" access code
     */
    deleteBubbleLink(): Promise<void>;

    /**
     * Method to reset bubble "guest" access code
     */
    resetBubbleLink(): Promise<string>;

    /**
     * Method to get bubble "guest" access code
     */
    getBubbleLink(): Promise<string>;

    /**
     * Method to add already existing tags to bubble
     * @param tagNames - array of tag names to add
     */
    addTags(tagNames: string[]): Promise<void>;

    /**
     * Method to remove tags from bubble
     * @param tagNames - array of tag names to remove
     */
    removeTags(tagNames: string[]): Promise<void>;

    /**
     * Method to clear specified data from bubble
     * @param clearData - array of data to clear. Possibile values are: im, poll, document, pin
     */
    clearBubbleData(clearData: BubbleCleanupData[]): Promise<void>;

    /**
     * Method to return a simplified JSON representation of the Bubble object
     */
    toJSON(): any;

    /**
     * Check if the bubble is internal to the connected user
     */
    isInternalBubble(): boolean;

    /**
     * Check if the bubble is external to the connected user
     */
    isExternalBubble(): boolean;
}

/**
* @internal
*/
export interface LobbyUser {

    /**
     * The Contact Object
     * @readonly
     */
    contact: Contact;

    /**
     * The date (in string format) when the user has enterred in the lobby
     * @readonly
     */
    additionDate: string;
}

/**
* @internal
*/
export enum AccessRequestStatus {

    /**
     * The request is pending
     */
    PENDING = 'pending',

    /**
     * The request is denied
     */
    DENIED = 'denied',

    /**
     * The request is accepted
     */
    ACCEPTED = 'accepted'
}

/**
 * @internal
 * This class represents a Bubble
 * It is a group conversation that allows users to send messages to all the members, start a web conference, spread information faster
 */
export class BubbleRB implements Bubble, BubbleInvitation {

    // ----------------------------------------------------
    // UCaaS + SDK Properties
    // ----------------------------------------------------
    public dbId: string;
    public jid: string;
    public owner: Contact = null;

    public name: string;
    public avatar: string;

    public description: string;
    public autoAcceptInvitation: boolean = false;
    public history: BubbleHistoryType = BubbleHistoryType.NONE;
    public autoRegister: string = 'unlock';
    public contentToAutoClear: {
        im: boolean;
        poll: boolean;
        document: boolean;
        member: boolean;
    };

    public visibility: BubbleVisibility = BubbleVisibility.PRIVATE;

    /**
     * Indicates whether the bubble is ready for chatting.
     */
    public ready: boolean = false;

    /**
     * The number of users of the bubble with the status 'accepted' (he joined the bubble) or 'invited'.
     * They are considered to be the actual participants in the bubble.
     */
    public nbParticipants: number = 0;

    public customData: any = {}; // PMA SDKv2:  TODO => SDKv2 à Documenter

    /**
     * Rainbow HUB specificity for Hunting Group.
     * Specifies whether the bubble is created as managed bubble.
     * In this case, there is no owner of the bubble; each member acts as organizer.
     * In addition, a group member cannot leave the bubble and cannot be removed from the bubble.
     * @readonly
     */
    public isHuntingGroup: boolean = false;

    /**
     * The bubble is managed by a Rainbow user acting as the bubble administrator.
     * This user is not a member of the bubble (It is not known to other members) and does not, by default, have the role of owner.
     * But it can manage the list (settings, members), as if it were the owner. In fact, it can give itself this role.
     * @readonly
     */
    public isOwnedByBubbleAdmin: boolean = false;

    /**
     * If the bubble has alert notification feature enabled
     * The notification feature must be enabled
     * @readonly
     */
    public isAlertNotificationEnabled: boolean = false;

    /**
     * Infos on the connected user in this bubble.
     * Used only as a shortcut to access the information of the user connected to this bubble.
     * This information is actually a ref of the 'users' entry.
     */
    public bubbleConnectedUser: BubbleUser;

    /**
     * The bubble is managed by a Rainbow user acting as the bubble administrator.
     * This user is not a member of the bubble (It is not known to other members) and does not, by default, have the role of owner.
     * But it can manage the list (settings, members), as if it were the owner. In fact, it can give itself this role.
     */
    public contentToClear: { im: boolean; document: boolean; poll: boolean; member: boolean; record: boolean; } = { im: false, document: false, poll: false, member: false, record: false };

    /**
     * True if the bubble has waiting lobby activated (for guests)
     */
    public hasLobby: boolean = false;

    /**
     * True if the bubble has password protection activated
     */
    private _hasPassword: boolean = false;

    public set hasPassword(value: boolean) {
        if (this._hasPassword !== value) {
            this._hasPassword = value;
            this.sendEvent(BubbleEvents.ON_BUBBLE_PASSWORD_UPDATED);
        }
    }

    public get hasPassword(): boolean {
        return this._hasPassword;
    }

    /**
     * True if the bubble has password protection activated
     */
    private _bubblePassword: string = '';

    public set bubblePassword(value: string) {
        if (this._bubblePassword !== value) {
            this._bubblePassword = value;
            this.sendEvent(BubbleEvents.ON_BUBBLE_PASSWORD_UPDATED);
        }
    }

    public get bubblePassword(): string {
        return this._bubblePassword;
    }

    public receivedFilePolicy: BubbleReceivedFilePolicyValue = BubbleReceivedFilePolicyValue.ENABLED;
    public shareFileCustomisation: BubbleShareFileCustomisationValue = BubbleShareFileCustomisationValue.ENABLED;
    public companyId: string;

    private _creationDate?: Date;
    public get creationDate(): Date { if (!this._creationDate) this._creationDate = new Date(); return this._creationDate; }
    public set creationDate(date: Date) { this._creationDate = date; }

    private _lastActivityDate?: Date;
    public get lastActivityDate(): Date { if (!this._lastActivityDate) this._lastActivityDate = this.creationDate; return this._lastActivityDate; }
    public set lastActivityDate(date: Date) { this._lastActivityDate = date; }

    public get lobbyUsers(): LobbyUser[] {
        return this.bubbleService?.getUsersInLobbyForBubble(this);
    }

    // Specificities for BubbleInvitation interface
    public get invitationDate(): Date {
        return this.bubbleConnectedUser.date;
    }

    public get invitationRole(): BubbleUserRole {
        return this.bubbleConnectedUser.role;
    }

    public get invitingUserId(): string {
        return this.bubbleConnectedUser.invitingUserId;
    }

    // ----------------------------------------------------
    // UCaaS Only: Internal management
    // ----------------------------------------------------

    public initPresPromise: any = null;
    public initPresPromiseResolve: any = null;
    public initPresInterval: any = null;
    public isActive: boolean = false;

    public filterName: string = null;
    public nameForLogs: string = "";
    public tags: BubbleTag[] = [];
    public lastAvatarUpdate: string;

    // Data accessible via getUsers()
    public users: BubbleUser[] = [];
    public organizers: BubbleUser[] = [];
    public members: BubbleUser[] = [];

    public guestPhoneNumbers: string[];
    public guestEmails: string[] = [];
    public avatarContacts: any[] = [];

    //object containing informations about end of cycle for guests
    public endOfConferenceBehavior;

    // SDKv2: Not Used by for the moment, maybe later
    // -----------------------------------------------
    // Folder management
    public containerName: string = ``;
    public containerId: string = ``;

    public conference: any;
    public webinarId: string = null;
    public groupId: string = '';
    public externalStorageAccessUrl?: string;
    // SDKv2: Not Used by for the moment, maybe later

    public rxSubject = new Subject<any>();
    public usersReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // ----------------------------------------------------
    // UCaaS Only: UI management
    // ----------------------------------------------------
    public noNotificationPopUp: boolean = false;
    public selected: boolean = false;

    // ----------------------------------------------------
    // BUBBLE INSTANCE LIFE CYCLE
    // ----------------------------------------------------

    private bubbleService?: any;
    private bubbleTagService?: any;
    private userService?: any;

    private constructor() {
        this.userService = MainService.getInstance().inject<ContactService>(CONTACT_SVC);
        this.bubbleService = MainService.getInstance().inject<BubbleService>(BUBBLE_SVC);
        this.bubbleTagService = MainService.getInstance().inject<BubbleTagService>(BUBBLE_TAG_SVC);
    }

    public static create(): BubbleRB { return new BubbleRB(); }

    // ----------------------------------------------------
    // BUBBLE INSTANCE PUBLIC API (INCLUDING SDK)
    // ----------------------------------------------------
    public sendEvent(name: BubbleEvents, data?: any): void {
        this.rxSubject.next(RBEvent.create<BubbleEvents, any>(name, data));
    }

    public subscribe(handler: (event: RBEvent<BubbleEvents>) => any, eventNames?: BubbleEvents | BubbleEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<BubbleEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;
        return observable.subscribe((event: RBEvent<BubbleEvents>) => { try { handler(event); } catch {} });
    }

    public async setCustomAvatar(base64AvatarImg: string, width?: number, height?: number): Promise<Bubble> {
        return this.bubbleService?.setBubbleAvatar(this, base64AvatarImg, width, height);
    }

    public async removeCustomAvatar(): Promise<Bubble> {
        return this.bubbleService?.deleteBubbleAvatar(this);
    }

    public async setSettings(bubbleSettings: BubbleSettings): Promise<Bubble> {
        return this.bubbleService?.updateBubbleSettings(this, bubbleSettings);
    }

    public getSettings(): BubbleSettings {
        return this.bubbleService?.getBubbleSettings(this);
    }

    public async setCustomData(customData: any): Promise<Bubble> {
        return this.bubbleService?.updateBubbleCustomData(this, customData);
    }

    public async setUserRole(user: User, role: BubbleUserRole): Promise<Bubble> {
        return this.bubbleService?.updateUserRoleInBubble(this, user, role);
    }

    public async changeOwner(newOwner: User): Promise<Bubble> {
        return this.bubbleService?.changeBubbleOwner(this, newOwner);
    }

    public async addUser(user: User, role?: BubbleUserRole, reason?: string, withoutInvitation?: boolean): Promise<Bubble> {
        return this.bubbleService?.addUserToBubble(this, user, reason, role, withoutInvitation);
    }

    public async inviteUsers(users: User[], emails?: string[], role?: BubbleUserRole): Promise<Bubble> {
        return this.bubbleService?.inviteUsersToBubble(this, users, emails, role);
    }

    public async removeUser(user: User): Promise<Bubble> {
        return this.bubbleService?.removeUserFromBubble(this, user);
    }

    public async getUsers(role: 'ALL' | 'USER' | 'ORGANIZER' = 'ALL'): Promise<BubbleUser[]> {
        return this.bubbleService?.getBubbleUsers(this, role);
    }

    public getAllAcceptedUsers(): BubbleUser[] {
        return this.users.filter((bubbleUser: BubbleUser) => {
            const contact = bubbleUser.user as Contact;
            return !contact.isBadGuest()
                && !contact.isAnonymousGuest()
                && !contact.isTerminated
                && bubbleUser.status === BubbleUserStatus.ACCEPTED;
        });
    }

    public async promoteAllUsers(): Promise<Bubble> {
        return this.bubbleService?.promoteAllUsers(this);
    }

    public async demoteAllUsers(): Promise<Bubble> {
        return this.bubbleService?.demoteAllUsers(this);
    }

    public async acceptInvitation(): Promise<Bubble> {
        return this.bubbleService.acceptBubbleInvitation(this);
    }

    public async declineInvitation(): Promise<Bubble> {
        return this.bubbleService.declineBubbleInvitation(this);
    }

    public async leave(createArchive?: boolean): Promise<Bubble | undefined> {
        return this.bubbleService.leaveBubble(this, createArchive);
    }

    public async delete(createArchive?: boolean): Promise<Bubble | undefined> {
        return this.bubbleService?.deleteMyBubble(this, createArchive);
    }

    public isUserOwner(user: User): boolean {
        return user ? (this.owner && this.owner.jid === user.jid) : false;
    }

    public isConnecteUserOwner(): boolean {
        return (this.owner?.jid === this.bubbleConnectedUser?.user.jid);
    }

    public isUserOrganizer(user: User): boolean {
        const bubbleUser: BubbleUser = this.getUserByJid(user.jid);
        return ((bubbleUser?.role === BubbleUserRole.ORGANIZER)
                && (bubbleUser?.status !== BubbleUserStatus.UNSUBSCRIBED));
    }

    public isConnecteUserOrganizer(): boolean {
        return ((this.bubbleConnectedUser?.role === BubbleUserRole.ORGANIZER)
                && (this.bubbleConnectedUser?.status !== BubbleUserStatus.UNSUBSCRIBED));
    }

    public async isConnectedUserMember(): Promise<boolean> {
        const bubbleUsers = await this.getUsers("ALL");
        return bubbleUsers.find(bubbleUser => bubbleUser.user.id === this.userService?.userContact.id) !== undefined;
    }

    public isBubbleConferenceStarted(): boolean {
        return (this.conference?.sessions?.length > 0 && (this.conference.sessions[0].type === "conference" || this.conference.sessions[0].type === "lounge"));
    }

    public getBubbleConferenceOwnerId(): string {
        if (this.conference?.sessions?.length && (this.conference.sessions[0].type === "conference" || this.conference.sessions[0].type === "lounge")) {
            return this.conference.sessions[0].ownerId;
        }
        return null;
    }

    public isWebinarBubble(): boolean {
        return Boolean(this.conference?.webinar?.id);
    }

    public async createBubbleLink(): Promise<string> {
        return this.bubbleService?.bindOpenInviteIdToBubble(this);
    }

    public async manageBubblePassword(activate: boolean): Promise<void> {
        return this.bubbleService.manageBubblePassword(this, activate);
    }

    public async resetBubblePassword(): Promise<void> {
        return this.bubbleService.resetBubblePassword(this);
    }

    public async resetBubbleLink(): Promise<string> {
        return this.bubbleService?.resetOpenInviteId(this);
    }

    public async deleteBubbleLink(): Promise<void> {
        return this.bubbleService?.unbindOpenInviteFromBubble(this);
    }

    public async getBubbleLink(): Promise<string> {
        return await this.bubbleService?.retrieveOpenInviteLinkForBubble(this);
    }

    public getBubbleOpenInviteIdLinkFromCache(): string | null {
        return this.bubbleService?.getOpenInviteIdLinkForRoom(this.dbId);
    }

    /**
     * Allow access to one or many lobby users that are in the waiting lobby;
     * If the lobbyUsers is empty; then this will allow ALL users to join the bubble
     */
    public async allowAccessToBubbleForWaitingLobbyUsers(lobbyUsers: LobbyUser[] = []) {
        return await this.bubbleService.acceptUsersWaitingInBubbleLobby(this, lobbyUsers);
    }

    /**
     * Deny access to the given lobby user waiting in the lobby room
     */
    public async allowAccessToBubbleForLobbyUser(lobbyUser: LobbyUser) {
        if (!lobbyUser) return false;
        return await this.bubbleService.acceptUsersWaitingInBubbleLobby(this, [lobbyUser]);
    }

    /**
     * Deny access to one or many lobby users that are in the waiting lobby;
     * If the lobbyUsers is empty; then this will DENY ALL users from joining the bubble
     */
    public async denyAccessToBubbleForWaitingLobbyUsers(lobbyUsers: LobbyUser[] = []) {
        return await this.bubbleService.denyUsersWaitingInBubbleLobby(this, lobbyUsers);
    }

    /**
     * Clear specified data from bubble
     */
    public async clearBubbleData(clearData: BubbleCleanupData[]) {
        return this.bubbleService?.clearBubbleData(this, clearData);
    }

    /**
     * Deny access to the given lobby user waiting in the lobby room
     */
    public async denyAccessToBubbleForLobbyUser(lobbyUser: LobbyUser) {
        if (!lobbyUser) return false;
        return await this.bubbleService.denyUsersWaitingInBubbleLobby(this, [lobbyUser]);
    }

    public getDialInCode(): string {
        return this.conference ? this.conference.dialInCode : "";
    }

    public getPhoneNumbers(): DialInPhoneNumber[] {
        return this.conference ? this.conference.phoneNumbers : [];
    }

    public async addTags(tagNames: string[]): Promise<void> {
        const allTags = this.bubbleTagService?.allTags;
        const tagsToSet = allTags.reduce((tags: BubbleTag[], tag: BubbleTag) => {
            if (tagNames.includes(tag.name)) tags.push(tag); return tags;
        }, []);
        return this.bubbleTagService?.setBubbleTags(this, tagsToSet.concat(this.tags));
    }

    public async removeTags(tagNames: string[]): Promise<void> {
        await Promise.all(tagNames.map(tag => this.bubbleTagService?.deleteBubbleTag(tag, [this])));
    }

    public getNameForLogs(): string {
        if (!this.nameForLogs && this.name) {
            const temp = this.name.replace(/[^\s](?=.{1,}$)/g, "*");
            this.nameForLogs = this.name.charAt(0) + temp.substr(1);
        }
        return this.nameForLogs;
    }

    // Specificities for BubbleInvitation interface
    public isWebinarInvitation(): boolean {
        return this.isWebinarBubble();
    }

    public isInternalBubble(): boolean {
        const bubbleUserContact: Contact = (this.bubbleConnectedUser.user) as Contact;
        return !this.companyId || this.companyId === bubbleUserContact.company?.id;
    }

    public isExternalBubble(): boolean {
        const bubbleUserContact: Contact = (this.bubbleConnectedUser.user) as Contact;
        return Boolean(this.companyId) && this.companyId !== bubbleUserContact.company?.id;
    }

    // ----------------------------------------------------
    // BUBBLE INSTANCE PUBLIC API NOT USED BY SDK
    // ----------------------------------------------------

    public update(bubbleData: any): this {
        this.dbId = bubbleData.id;
        this.jid = bubbleData.jid;
        this.name = bubbleData.name;
        this.description = bubbleData.topic; // Warning: topic = field name provided by Server
        if (bubbleData.creationDate) this.creationDate = new Date(bubbleData.creationDate);
        if (bubbleData.lastActivityDate) this.lastActivityDate = new Date(bubbleData.lastActivityDate);
        this.history = bubbleData.history;
        this.customData = bubbleData.customData ? bubbleData.customData : this.customData;
        this.conference = bubbleData.conference;
        if (!bubbleData.lastAvatarUpdateDate) this.avatar = null;
        this.isActive = bubbleData.isActive;
        this.autoRegister = bubbleData.autoRegister;
        this.autoAcceptInvitation = bubbleData.autoAcceptInvitation;
        this.visibility = bubbleData.visibility === 'public' ? BubbleVisibility.PUBLIC : BubbleVisibility.PRIVATE;

        this.containerId = bubbleData.containerId ? bubbleData.containerId : '';
        this.containerName = bubbleData.containerName ? bubbleData.containerName : '';
        this.isHuntingGroup = bubbleData.isOwnedByGroup;
        this.isOwnedByBubbleAdmin = bubbleData.isOwnedByRoomAdmin;
        this.isAlertNotificationEnabled = bubbleData.isAlertNotificationEnabled ? bubbleData.isAlertNotificationEnabled : false;
        this.nbParticipants = bubbleData.activeUsersCounter ? bubbleData.activeUsersCounter : 1;
        this.contentToClear = bubbleData.contentToClear ? bubbleData.contentToClear : { im: false, document: false, poll: false, member: false, record: false };

        // Handle bubble tags
        if (bubbleData.tags) {
            this.tags = bubbleData.tags.map((tagData: any) => { return { name: tagData.tag, color: tagData.color }; });
        }

        // Usefull only for guests
        this.endOfConferenceBehavior = bubbleData.endOfConferenceBehavior;

        this.hasLobby = Boolean(bubbleData.hasLobby);
        this.hasPassword = Boolean(bubbleData.hasPassword);
        this.receivedFilePolicy = bubbleData.receivedFilePolicy;
        this.companyId = bubbleData.companyId ? bubbleData.companyId : '';

        //update the password
        if (bubbleData?.publicLink) {
            if (this.hasPassword) { this.bubblePassword = bubbleData?.publicLink.roomPassword; }
            else { this.bubblePassword = ''; }
        }

        return this;
    }

    public updateAvatarInfo(): void {
        if (this.avatar) return;

        const avatarContacts = [];
        const orderedUsers: BubbleUser[] = this.users.slice();
        orderedUsers.sort((aa, bb) => {
            if (new Date(aa.date) > new Date(bb.date)) return 1;
            if (new Date(aa.date) < new Date(bb.date)) return -1;
            return 0;
        });

        for (let index = 0; index < orderedUsers.length; index++) {
            const contact = orderedUsers[index].user as Contact;
            const status = orderedUsers[index].status;
            if ((!this.owner || (contact.dbId !== this.owner.dbId))
                && !contact.isAnonymousGuest()
                && !contact.isBadGuest()
                && !contact.isTerminated) {
                if ((this.isConnecteUserOwner() || this.isConnecteUserOrganizer()) && (status === BubbleUserStatus.ACCEPTED || status === BubbleUserStatus.INVITED)) { avatarContacts.push(contact); }
                else if (status === BubbleUserStatus.ACCEPTED) { avatarContacts.push(contact); }
                if (avatarContacts.length === 5) { break; }
            }
        }

        let shouldUpdate = false;
        //compare the current avatar contacts and the new contacts
        if (this.avatarContacts.length !== avatarContacts.length) {
            shouldUpdate = true;
        } else {
            this.avatarContacts.forEach((contact, index) => {
                if (avatarContacts[index]?.id !== contact.id) {
                    shouldUpdate = true;
                }
            });
        }

        this.avatarContacts = avatarContacts;

        if (this.rxSubject && shouldUpdate) { this.rxSubject.next(RBEvent.create(BubbleEvents.ON_AVATAR_UPDATE, this.avatarContacts)); }
    }

    public async addRainbowTVs(androidTVs: User[]): Promise<Bubble> {
        return this.bubbleService?.addRainbowTVsToBubble(this, androidTVs);
    }

    public isUserStatusAcceptedOrInvited(contact: any): boolean {
        const user: BubbleUser = this.getUserByJid(contact.jid);
        return (user?.status === BubbleUserStatus.ACCEPTED || user?.status === BubbleUserStatus.INVITED);
    }

    public isUserStillBelongToBubble(userJid: string): boolean {
        const ownUser: BubbleUser = this.getUserByJid(userJid);
        if (!ownUser || (ownUser.status === BubbleUserStatus.UNSUBSCRIBED || ownUser.status === BubbleUserStatus.DELETED)) { return false; }
        return true;
    }

    public getUserByJid(userJid: string): BubbleUser {
        for (let i = 0; i < this.users.length; i++) { if (userJid === this.users[i].user.jid) { return this.users[i]; } }
        return null;
    }

    public setWebConferenceOwnerId(ownerId: string) {
        if (this.conference?.sessions?.length && (this.conference.sessions[0].type === "conference" || this.conference.sessions[0].type === "lounge")) {
            this.conference.sessions[0].ownerId = ownerId;
        }
    }

    public addConferenceSession(ownerId: string) {
        if (!this.conference) { this.conference = {}; }
        this.conference.sessions = [{ calledUserIds: [], id: this.dbId, ownerId, type: "conference", startDate: new Date().toISOString() }];
    }

    public getCurrentConferenceSession(): any {
        if (this.conference?.sessions?.length && (this.conference.sessions[0].type === "conference" || this.conference.sessions[0].type === "lounge")) {
            return this.conference.sessions[0];
        }
        return null;
    }

    public setAutoRegister(value: string): void {
        this.autoRegister = value;
    }

    public setMuteUponEntry(value: boolean): void {
        if (this.conference) {
            this.conference.muteUponEntry = value;
        }
    }

    public setPlayEntryTone(value: boolean): void {
        if (this.conference) {
            this.conference.playEntryTone = value;
        }
    }

    public setDialInCode(value: string): void {
        if (this.conference && (this.conference.dialInCode !== value)) {
            this.conference.dialInCode = value;
            if (this.rxSubject) { this.rxSubject.next(RBEvent.create(BubbleEvents.ON_DIAL_IN_CODE_UPDATE, this.conference.dialInCode)); }
        }
    }

    public setIncludeAllPhoneNumbers(value: boolean): void {
        if (this.conference && (this.conference.includeAllPhoneNumbers !== value)) {
            this.conference.includeAllPhoneNumbers = value;
            if (this.rxSubject) { this.rxSubject.next(RBEvent.create(BubbleEvents.ON_INCLUDE_ALL_PHONENUMBER_UPDATE, this.conference.includeAllPhoneNumbers)); }
        }
    }

    public setPhoneNumbers(value: DialInPhoneNumber[]): void {
        if (this.conference) {
            this.conference.phoneNumbers = value;
            if (this.rxSubject) { this.rxSubject.next(RBEvent.create(BubbleEvents.ON_DIAL_IN_PHONENUMBER_UPDATE, this.conference.phoneNumbers)); }
        }
    }

    public setContentToAutoclear(contentToAutoclear: {
        im: boolean;
        poll: boolean;
        document: boolean;
        member: boolean;
    }): void {
        if (this.conference) {
            this.conference.contentToAutoClear = contentToAutoclear;
        }
    }

    public setConferencePreConfig(value: ConferenceRecordingConfig): void {
        if (this.conference) {
            this.conference = {
                ...this.conference,
                recordingAndTranscription: value
            };
        }
    }

    // ----------------------------------------------------
    // BUBBLE INSTANCE DEBUG (MAY BE USED BY SDK)
    // ----------------------------------------------------

    public toString(): string {
        return "Bubble " + this.dbId + " " + this.name + " " + this.jid;
    }

    public toJSON(): any {
        // Special JSON output with only useful information for an invitation as defined in BubbleInvitation interface.
        if (this.bubbleConnectedUser && this.bubbleConnectedUser.status === BubbleUserStatus.INVITED) {
            const jsonInvitation: any = {
                id: this.dbId,
                name: this.name,
                status: this.bubbleConnectedUser.status,
                role: this.bubbleConnectedUser.role,
                invitationDate: this.bubbleConnectedUser.date,
                invitingUserId: this.bubbleConnectedUser.invitingUserId,
            };
            if (this.avatar) { jsonInvitation.avatar = this.avatar; }
            if (this.description) { jsonInvitation.description = this.description; }

            return jsonInvitation;
        }

        // JSON output for bubble information
        const json: any = {
            id: this.dbId,
            history: this.history,
            autoRegister: this.autoRegister,
            ownerId: this.owner.dbId,
            name: this.name,
            nbParticipants: this.nbParticipants,
            usersReady: this.usersReady.value,
            tags: this.tags
        };

        if (this.avatar) { json.avatar = this.avatar; }
        if (this.description) { json.description = this.description; }

        if (this.bubbleConnectedUser) {
            json.connectedUser = {
                status: this.bubbleConnectedUser.status,
                role: this.bubbleConnectedUser.role,
                invitationDate: this.bubbleConnectedUser.date,
                invitingUserId: this.bubbleConnectedUser.invitingUserId,
            };
        }
        else json.connectedUser = 'UNDEFINED';

        if (this.members?.length) {
            json.members = this.members.map((member: BubbleUser) => {
                const userJson: any = {
                    contact: member.user.dbId,
                    displayName: member.user.displayName,
                    status: member.status,
                    role: member.role,
                    date: member.date,
                    invitingUserId: member.invitingUserId
                };
                if (member.user.nickname) userJson.nickName = member.user.nickname;
                return userJson;
            });
        }

        if (this.organizers?.length) {
            json.organizers = this.organizers.map((organizer: BubbleUser) => {
                const userJson: any = {
                    contact: organizer.user.dbId,
                    displayName: organizer.user.displayName,
                    status: organizer.status,
                    role: organizer.role,
                    date: organizer.date,
                    invitingUserId: organizer.invitingUserId
                };
                return userJson;
            });
        }

        return json;
    }
}
